import { searchClient } from "@/features/search/api/client";
import type { paths } from "@/features/search/api/schema";
import { queryOptions } from "@tanstack/react-query";

export type SearchParams = NonNullable<
  paths["/search"]["get"]["parameters"]["query"]
>;
export type PageParams = NonNullable<
  paths["/pages"]["get"]["parameters"]["query"]
>;
export type SuggestionsParams = NonNullable<
  paths["/suggestions"]["get"]["parameters"]["query"]
>;
export type SearchResponse =
  paths["/search"]["get"]["responses"]["200"]["content"]["application/json"];

/*
 * `/search` and `/pages` are queried through `searchRouteQueryOptions` in `./route.ts`,
 * which picks the endpoint from the URL.
 */

/**
 * Autocomplete for the search box. The service ranks and caches (`max-age=60`), so this only needs a
 * matching `staleTime` to stop a re-opened dropdown re-requesting a prefix already in the cache.
 *
 * Keyed under its own root, not under `["search", …]`: that root is the results cache, and callers
 * filter on it wholesale (`invalidateQueries`, `setQueriesData` — see `SEARCH_RESULTS_QUERY_KEY`).
 * Sharing it would drag suggestions, which have a different shape, into those updates.
 */
export const suggestionsQueryOptions = (params: SuggestionsParams) =>
  queryOptions({
    queryKey: ["suggestions", params] as const,
    queryFn: async ({ signal }) => {
      const { data, error } = await searchClient.GET("/suggestions", {
        params: { query: params },
        signal,
      });

      if (error) {
        throw error;
      }

      return data;
    },
  });

/**
 * What the service would turn this query into, asked before navigating to it.
 *
 * The results page converts a natural-language query into filters on arrival, which works but is
 * visible: the URL rewrites itself a moment after landing. Asking here first means the shopper is
 * sent to the filtered URL to begin with.
 *
 * Bounded rather than awaited indefinitely — the answer costs an LLM call, and a search box that
 * hangs on one is worse than a URL that catches up later. On timeout, error, or anything else, the
 * caller navigates with the text as typed and the page does the conversion as it always has.
 */
const RESOLVE_TIMEOUT_MS = 4000;

export const resolveSearchParams = async (
  query: string
): Promise<string | null> => {
  try {
    const { data } = await searchClient.GET("/resolve", {
      params: { query: { query } },
      signal: AbortSignal.timeout(RESOLVE_TIMEOUT_MS),
    });

    return data?.params ?? null;
  } catch {
    return null;
  }
};
